import React, { useState } from 'react';
import { X, CheckCircle2, Bookmark, BookOpen, Volume2, ArrowRight, ArrowLeft, LogOut, Sparkles } from 'lucide-react';
import { SessionSavedItem } from '../../services/friendSessionService';
import { useSpeechSynthesis } from '../../hooks/useSpeechSynthesis';

interface FinishSessionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirmEnd: () => Promise<void> | void;
  savedItems: SessionSavedItem[];
  materialTitle?: string;
  partnerName: string;
}

export const FinishSessionModal: React.FC<FinishSessionModalProps> = ({
  isOpen,
  onClose,
  onConfirmEnd,
  savedItems,
  materialTitle = 'Study Session',
  partnerName,
}) => {
  const [mode, setMode] = useState<'menu' | 'review_words' | 'review_sentences'>('menu');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isEnding, setIsEnding] = useState(false);
  const { speak } = useSpeechSynthesis({ lang: 'en-US' });

  if (!isOpen) return null;

  const wordItems = savedItems.filter((i) => i.type === 'word' || (!i.type && !i.word.includes(' ')));
  const sentenceItems = savedItems.filter(
    (i) => i.type === 'expression' || i.word.includes(' ') || Boolean(i.exampleSentence)
  );

  const activeReviewItems = mode === 'review_words'
    ? (wordItems.length > 0 ? wordItems : savedItems)
    : (sentenceItems.length > 0 ? sentenceItems : savedItems);

  const currentItem = activeReviewItems[currentIndex];

  const handleFinish = async () => {
    setIsEnding(true);
    try {
      await onConfirmEnd();
    } finally {
      setIsEnding(false);
    }
  };

  const resetToMenu = () => {
    setMode('menu');
    setCurrentIndex(0);
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs animate-in fade-in duration-200">
      <div
        className="w-full max-w-lg bg-white rounded-3xl shadow-2xl border border-purple-100 overflow-hidden animate-in zoom-in-95 duration-200"
        role="dialog"
        aria-modal="true"
        aria-labelledby="finish-session-title"
      >
        {/* Header */}
        <div className="p-5 sm:p-6 bg-gradient-to-r from-purple-600 via-indigo-600 to-purple-700 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-white/20 flex items-center justify-center text-white shrink-0">
              {mode === 'menu' ? <Sparkles className="w-5 h-5" /> : <BookOpen className="w-5 h-5" />}
            </div>
            <div>
              <h2 id="finish-session-title" className="text-base sm:text-lg font-bold">
                {mode === 'menu' && 'Finish Study Session 🎓'}
                {mode === 'review_words' && 'Review Saved Words 📚'}
                {mode === 'review_sentences' && 'Review Sentences & Expressions 📝'}
              </h2>
              <p className="text-xs text-purple-100 line-clamp-1">
                {mode === 'menu'
                  ? `With ${partnerName} • ${savedItems.length} items collected`
                  : `Card ${currentIndex + 1} of ${activeReviewItems.length}`}
              </p>
            </div>
          </div>
          <button
            onClick={mode === 'menu' ? onClose : resetToMenu}
            className="p-1.5 rounded-full text-white/80 hover:text-white hover:bg-white/20 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-5 sm:p-6">
          {mode === 'menu' && (
            <div className="space-y-4">
              {/* Session Recap Card */}
              <div className="p-4 bg-purple-50/70 rounded-2xl border border-purple-100/80">
                <span className="text-[10px] font-bold uppercase tracking-wider text-purple-600">Material Studied</span>
                <h3 className="font-bold text-gray-900 text-sm sm:text-base mt-0.5 line-clamp-2">
                  {materialTitle}
                </h3>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white text-purple-700 border border-purple-200 text-xs font-semibold">
                    <Bookmark className="w-3.5 h-3.5" />
                    {savedItems.length} Saved {savedItems.length === 1 ? 'Item' : 'Items'}
                  </span>
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white text-gray-700 border border-gray-200 text-xs font-medium">
                    Partner: <strong className="font-semibold text-purple-700">{partnerName}</strong>
                  </span>
                </div>
              </div>

              {/* Action Choices */}
              <div className="space-y-2.5 pt-1">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">What would you like to do?</p>

                {/* Option 1: Review Words */}
                <button
                  type="button"
                  onClick={() => {
                    setCurrentIndex(0);
                    setMode('review_words');
                  }}
                  disabled={savedItems.length === 0}
                  className="w-full p-4 rounded-2xl border-2 border-purple-100 hover:border-purple-500 bg-white hover:bg-purple-50/30 text-left transition-all flex items-center justify-between group disabled:opacity-50 disabled:pointer-events-none"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-purple-100 text-purple-700 flex items-center justify-center font-bold text-lg group-hover:scale-105 transition-transform">
                      📚
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 text-sm sm:text-base group-hover:text-purple-700 transition-colors">
                        Review Saved Words
                      </p>
                      <p className="text-xs text-gray-500">
                        {wordItems.length > 0 ? `${wordItems.length} single words to practice` : 'Practice all saved vocabulary'}
                      </p>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-purple-600 group-hover:translate-x-0.5 transition-all" />
                </button>

                {/* Option 2: Review Sentences / Expressions */}
                <button
                  type="button"
                  onClick={() => {
                    setCurrentIndex(0);
                    setMode('review_sentences');
                  }}
                  disabled={savedItems.length === 0}
                  className="w-full p-4 rounded-2xl border-2 border-indigo-100 hover:border-indigo-500 bg-white hover:bg-indigo-50/30 text-left transition-all flex items-center justify-between group disabled:opacity-50 disabled:pointer-events-none"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold text-lg group-hover:scale-105 transition-transform">
                      📝
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 text-sm sm:text-base group-hover:text-indigo-700 transition-colors">
                        Review Sentences & Expressions
                      </p>
                      <p className="text-xs text-gray-500">
                        {sentenceItems.length > 0 ? `${sentenceItems.length} expressions & context sentences` : 'Review example sentences'}
                      </p>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-indigo-600 group-hover:translate-x-0.5 transition-all" />
                </button>

                {/* Option 3: Skip & Finish */}
                <button
                  type="button"
                  onClick={handleFinish}
                  disabled={isEnding}
                  className="w-full p-3.5 rounded-2xl border border-gray-200 hover:border-gray-300 bg-gray-50 hover:bg-gray-100 text-left transition-all flex items-center justify-between group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-gray-200/80 text-gray-700 flex items-center justify-center">
                      <LogOut className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 text-sm">
                        {isEnding ? 'Finishing session...' : 'Skip and Finish'}
                      </p>
                      <p className="text-xs text-gray-500">Exit and return to Friends</p>
                    </div>
                  </div>
                  <span className="text-xs font-bold text-gray-600 group-hover:text-gray-900">Finish 🏁</span>
                </button>
              </div>

              {savedItems.length === 0 && (
                <p className="text-center text-xs text-gray-400 pt-2">
                  No words were saved in this session yet. You can still finish or keep studying!
                </p>
              )}
            </div>
          )}

          {/* Interactive Review Mode (Words or Sentences) */}
          {(mode === 'review_words' || mode === 'review_sentences') && currentItem && (
            <div className="space-y-4">
              {/* Progress Bar */}
              <div className="w-full bg-gray-100 h-1.5 rounded-full overflow-hidden">
                <div
                  className="bg-purple-600 h-full transition-all duration-300"
                  style={{ width: `${((currentIndex + 1) / activeReviewItems.length) * 100}%` }}
                />
              </div>

              {/* Review Card */}
              <div className="p-5 bg-gradient-to-br from-purple-50/50 via-white to-indigo-50/30 rounded-2xl border border-purple-100 shadow-xs space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-1">
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-purple-100 text-purple-700 uppercase tracking-wide">
                      {currentItem.type || 'Word'}
                    </span>
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900 break-words leading-tight">
                      {currentItem.word}
                    </h3>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      type="button"
                      onClick={() => speak(currentItem.word, 'en-US')}
                      className="p-2 rounded-xl bg-purple-100 hover:bg-purple-200 text-purple-700 transition-colors flex items-center gap-1 text-xs font-bold"
                      title="Pronounce (US)"
                    >
                      <Volume2 className="w-4 h-4" />
                      <span>US</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => speak(currentItem.word, 'en-GB')}
                      className="p-2 rounded-xl bg-indigo-100 hover:bg-indigo-200 text-indigo-700 transition-colors flex items-center gap-1 text-xs font-bold"
                      title="Pronounce (UK)"
                    >
                      <Volume2 className="w-4 h-4" />
                      <span>UK</span>
                    </button>
                  </div>
                </div>

                {currentItem.translation && (
                  <div className="pt-2 border-t border-purple-100/60">
                    <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Portuguese Translation</span>
                    <p className="text-sm sm:text-base font-semibold text-purple-900 mt-0.5">
                      {currentItem.translation}
                    </p>
                  </div>
                )}

                {currentItem.definition && (
                  <div className="pt-2 border-t border-purple-100/60">
                    <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Definition</span>
                    <p className="text-xs sm:text-sm text-gray-600 mt-0.5 leading-relaxed">
                      {currentItem.definition}
                    </p>
                  </div>
                )}

                {(currentItem.exampleSentence || mode === 'review_sentences') && currentItem.exampleSentence && (
                  <div className="pt-2 border-t border-purple-100/60">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Example Sentence</span>
                      <button
                        type="button"
                        onClick={() => speak(currentItem.exampleSentence!, 'en-US')}
                        className="text-purple-600 hover:text-purple-800 text-xs font-semibold flex items-center gap-1"
                      >
                        <Volume2 className="w-3.5 h-3.5" />
                        <span>Listen</span>
                      </button>
                    </div>
                    <p className="text-xs sm:text-sm font-medium text-gray-800 mt-1 italic">
                      "{currentItem.exampleSentence}"
                    </p>
                    {currentItem.exampleTranslation && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        {currentItem.exampleTranslation}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Navigation Controls */}
              <div className="flex items-center justify-between gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setCurrentIndex((prev) => Math.max(0, prev - 1))}
                  disabled={currentIndex === 0}
                  className="px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50 text-xs font-bold flex items-center gap-1.5 transition-colors disabled:opacity-40 disabled:pointer-events-none"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Previous</span>
                </button>

                {currentIndex < activeReviewItems.length - 1 ? (
                  <button
                    type="button"
                    onClick={() => setCurrentIndex((prev) => Math.min(activeReviewItems.length - 1, prev + 1))}
                    className="px-5 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold flex items-center gap-1.5 transition-colors shadow-xs"
                  >
                    <span>Next</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleFinish}
                    disabled={isEnding}
                    className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white text-xs font-bold flex items-center gap-1.5 transition-all shadow-md"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    <span>{isEnding ? 'Finishing...' : 'Complete & Finish Session'}</span>
                  </button>
                )}
              </div>

              {/* Back to options */}
              <div className="text-center pt-1">
                <button
                  type="button"
                  onClick={resetToMenu}
                  className="text-xs font-semibold text-gray-400 hover:text-gray-600 transition-colors"
                >
                  ← Back to options
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
